import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../../styles/scss/lessonCancellation.scss";
import { PREFECTURES } from "../../../constants/studioConstants";

// 休校振替管理（旧レッスン管理 list/edit を1画面に統合）
// フィルタ＋カレンダー＋一覧＋モーダルで、休校/振替の確認・登録・変更・削除を行う。
// カスケード（インストラクター→スタジオ→休校日→第5◯曜）はマスタを一括ロードしてクライアント計算する。

const DOW = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
const DOW_JP = ["日", "月", "火", "水", "木", "金", "土"];
const WEEKS_AHEAD = 26;

const pad = (n) => String(n).padStart(2, "0");
const toYmd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parseYmd = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const dowOf = (s) => parseYmd(s).getDay();
const shortDate = (s) => {
  const d = parseYmd(s);
  return `${d.getMonth() + 1}/${d.getDate()}(${DOW_JP[d.getDay()]})`;
};
const uniq = (a) => [...new Set(a)];

// カレンダーのチップ配色クラス
// ○(振替先)は緑、×(休校日)は理由(kind)で色分け：振替あり=赤 / 返金=琥珀 / 5週目=グレー / 未処理=オレンジ
const CHIP_KIND_CLASS = { reschedule: "k-resched", refund: "k-refund", none5: "k-none5", pending: "k-pending" };
function chipVariant(it) {
  if (it.t === "r") return "k-dst";
  return CHIP_KIND_CLASS[it.c.kind] || "k-resched";
}

// その日の既存休講が覆っているレッスン。lesson_id が null の行はその日の全クラスを
// 覆うので "all" を返す。返り値: "all" | Set<lessonId>
function coveredOn(cancellations, instId, studioId, ds, excludeId) {
  const rows = cancellations.filter(
    (c) =>
      c.studio_id === studioId &&
      c.instructor_id === instId &&
      c.cancellation_date === ds &&
      c.id !== excludeId
  );
  if (rows.some((c) => c.lesson_id == null)) return "all";
  return new Set(rows.map((c) => c.lesson_id));
}

// 今後26週のうち、指定曜日かつ その講師・スタジオでまだ休校の余地がある日。
// クラス単位休講があるので「1つでも未登録のクラスが残っている日」を候補にする
// （全クラス登録済みの日だけ落とす）。
function openDatesFor(lessonsByWeekday, cancellations, instId, studioId, excludeId, today) {
  if (!lessonsByWeekday.size) return [];
  const out = [];
  for (let i = 0; i < WEEKS_AHEAD * 7; i++) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    const dayLessons = lessonsByWeekday.get(d.getDay());
    if (!dayLessons || !dayLessons.length) continue;
    const ds = toYmd(d.getFullYear(), d.getMonth(), d.getDate());
    const covered = coveredOn(cancellations, instId, studioId, ds, excludeId);
    const full = covered === "all" || dayLessons.every((l) => covered.has(l.id));
    if (!full) out.push({ date: ds, label: `${d.getMonth() + 1}/${d.getDate()}（${DOW_JP[d.getDay()]}）` });
  }
  return out;
}

// レッスンマスタから開催日が引けない休校（閉講済みクラスなど）の日付編集用。
// クラス情報が無いので旧来どおり「その日に既存休講があれば除外」で候補を出す。
function fallbackDatesFor(weekday, cancellations, instId, studioId, excludeId, today) {
  const cancelled = new Set(
    cancellations
      .filter((c) => c.studio_id === studioId && c.instructor_id === instId && c.id !== excludeId)
      .map((c) => c.cancellation_date)
  );
  const out = [];
  for (let i = 0; i < WEEKS_AHEAD * 7; i++) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    if (d.getDay() !== weekday) continue;
    const ds = toYmd(d.getFullYear(), d.getMonth(), d.getDate());
    if (!cancelled.has(ds)) out.push({ date: ds, label: `${d.getMonth() + 1}/${d.getDate()}（${DOW_JP[d.getDay()]}）` });
  }
  return out;
}

// 休校にできる日（今後26週の開催日 − 全クラス登録済みの日）
function validCancelDates(lessons, cancellations, instId, studioId, excludeId, today) {
  const byWeekday = new Map();
  for (const l of lessons) {
    if (l.instructor_id !== instId || l.studio_id !== studioId) continue;
    const wd = DOW[l.day_of_week];
    if (wd === undefined) continue;
    if (!byWeekday.has(wd)) byWeekday.set(wd, []);
    byWeekday.get(wd).push(l);
  }
  return openDatesFor(byWeekday, cancellations, instId, studioId, excludeId, today);
}

// 選択中の値が候補に無い場合（レッスンが閉講済み・講師が退職済みなど）でも編集できるよう、
// 先頭に「（現在）」付きで残す。休校日の dateOptions と同じ方針。
function withCurrent(opts, id, label) {
  if (!id || opts.some((o) => o.id === id)) return opts;
  return [{ id, label: `${label}（現在）` }, ...opts];
}

// 指定曜日の「第5◯曜」の日付を今後 monthsCount ヶ月ぶん
function fifthWeekdayDates(weekday, today, monthsCount) {
  let y = today.getFullYear();
  let m = today.getMonth();
  const out = [];
  for (let i = 0; i < monthsCount; i++) {
    const firstDow = new Date(y, m, 1).getDay();
    const fifth = 1 + ((weekday - firstDow + 7) % 7) + 28;
    const dim = new Date(y, m + 1, 0).getDate();
    if (fifth <= dim) {
      const ds = toYmd(y, m, fifth);
      if (parseYmd(ds) >= today) out.push(ds);
    }
    m++;
    if (m > 11) {
      m = 0;
      y++;
    }
  }
  return out;
}

function LessonCancellationPage() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const today = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);

  // マスタ
  const [studios, setStudios] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [cancellations, setCancellations] = useState([]);

  // フィルタ・表示
  const [filterStudio, setFilterStudio] = useState(null);
  const [filterInstructor, setFilterInstructor] = useState(null);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selDate, setSelDate] = useState(null);
  const [focusedCancelId, setFocusedCancelId] = useState(null); // カレンダーのチップから選択された休講

  // モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // add | edit
  const [editId, setEditId] = useState(null);
  const [editCancel, setEditCancel] = useState(null); // 編集中の休講オブジェクト（削除確認の名前表示に使用）
  const [form, setForm] = useState({ instId: null, studioId: null, date: null, resched: "", reason: "", lessonIds: [] });

  // トースト
  const [toast, setToast] = useState("");
  const toastRef = useRef(null);
  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(""), 2600);
  }, []);

  // カレンダーのチップ押下 → 右カラムをその日に絞り込み、対象の休講をハイライト
  const focusCancellation = useCallback((c, ds) => {
    setSelDate(ds);
    setFocusedCancelId(c.id);
  }, []);
  // 選択日が解除されたらハイライトも解除
  useEffect(() => {
    if (selDate === null) setFocusedCancelId(null);
  }, [selDate]);

  const studioName = useCallback((id) => (studios.find((s) => s.id === id) || {}).name || "", [studios]);
  const uname = useCallback((id) => (instructors.find((i) => i.id === id) || {}).username || "?", [instructors]);
  // 休講のインストラクター名は、退職者(instructor-list集合外)でも表示できるようサーバ解決済みの username を優先する
  const cname = useCallback((c) => c.instructor_username || uname(c.instructor_id), [uname]);
  const todayStr = toYmd(today.getFullYear(), today.getMonth(), today.getDate());

  // ---------- データ取得 ----------
  const loadCancellations = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/get_cancellations/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCancellations(data.cancellations || []);
    } catch (e) {
      console.error("休校振替の取得に失敗しました", e);
    }
  }, [apiUrl]);

  useEffect(() => {
    const loadMaster = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/get_lesson_master/`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // スタジオは都道府県順（PREFECTURES）→ その他 の順に並べる
        const order = PREFECTURES.map((p) => p.text);
        const sorted = [...(data.studios || [])].sort((a, b) => {
          const ai = order.indexOf(a.prefecture);
          const bi = order.indexOf(b.prefecture);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.id - b.id;
        });
        setStudios(sorted);
        setInstructors(data.instructors || []);
        setLessons(data.lessons || []);
      } catch (e) {
        console.error("マスタの取得に失敗しました", e);
      }
    };
    loadMaster();
    loadCancellations();
  }, [apiUrl, loadCancellations]);

  // ---------- フィルタ済みデータ ----------
  const filtered = useMemo(
    () =>
      cancellations.filter((c) => {
        if (filterStudio && c.studio_id !== filterStudio) return false;
        if (filterInstructor && c.instructor_id !== filterInstructor) return false;
        return true;
      }),
    [cancellations, filterStudio, filterInstructor]
  );

  const inMonth = useCallback(
    (s) => s && parseYmd(s).getMonth() === month && parseYmd(s).getFullYear() === year,
    [month, year]
  );

  const summary = useMemo(() => {
    let c = 0;
    let r = 0;
    filtered.forEach((x) => {
      if (inMonth(x.cancellation_date)) c++;
      if (x.kind === "reschedule" && inMonth(x.rescheduled_date)) r++;
    });
    return { cancel: c, resched: r };
  }, [filtered, inMonth]);

  // 都道府県グループ（フィルタ用）
  const studioGroups = useMemo(() => {
    const grouped = {};
    studios.forEach((s) => {
      const key = s.prefecture || "その他";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(s);
    });
    return Object.entries(grouped);
  }, [studios]);

  // ---------- カレンダー ----------
  const calendarCells = useMemo(() => {
    const first = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push({ blank: true, key: `b${i}` });
    for (let d = 1; d <= days; d++) {
      const ds = toYmd(year, month, d);
      const dayCancels = filtered.filter((c) => c.cancellation_date === ds);
      const dayResched = filtered.filter((c) => c.rescheduled_date === ds && c.kind === "reschedule");
      cells.push({ key: ds, d, ds, dow: parseYmd(ds).getDay(), dayCancels, dayResched });
    }
    return cells;
  }, [year, month, filtered]);

  const studioLessonsOn = useCallback(
    (studioId, ds) => lessons.filter((l) => l.studio_id === studioId && DOW[l.day_of_week] === dowOf(ds)),
    [lessons]
  );

  // ---------- 一覧 ----------
  const listRows = useMemo(() => {
    let list = filtered.filter(
      (c) => inMonth(c.cancellation_date) || (c.kind === "reschedule" && inMonth(c.rescheduled_date))
    );
    if (selDate) list = list.filter((c) => c.cancellation_date === selDate || c.rescheduled_date === selDate);
    return [...list].sort((a, b) => a.cancellation_date.localeCompare(b.cancellation_date));
  }, [filtered, inMonth, selDate]);

  // ---------- モーダル：選択肢 ----------
  // モーダルのプルダウンはフィルタと同じ並び順にする
  // （マスタ配列の順＝インストラクターは dancer_name 順、スタジオは都道府県順）
  const instructorRank = useMemo(() => {
    const m = new Map();
    instructors.forEach((i, idx) => m.set(i.id, idx));
    return m;
  }, [instructors]);
  const studioRank = useMemo(() => {
    const m = new Map();
    studios.forEach((s, idx) => m.set(s.id, idx));
    return m;
  }, [studios]);
  const byInstructorRank = useCallback(
    (a, b) => (instructorRank.get(a) ?? Infinity) - (instructorRank.get(b) ?? Infinity),
    [instructorRank]
  );
  const byStudioRank = useCallback(
    (a, b) => (studioRank.get(a) ?? Infinity) - (studioRank.get(b) ?? Infinity),
    [studioRank]
  );

  const instOptions = useMemo(() => {
    let ids;
    if (form.studioId && form.date) {
      const wd = dowOf(form.date);
      const busy = new Set(
        cancellations
          .filter((c) => c.studio_id === form.studioId && c.cancellation_date === form.date && c.id !== editId)
          .map((c) => c.instructor_id)
      );
      ids = uniq(
        lessons
          .filter((l) => l.studio_id === form.studioId && DOW[l.day_of_week] === wd && !busy.has(l.instructor_id))
          .map((l) => l.instructor_id)
      );
    } else if (form.studioId) {
      ids = uniq(lessons.filter((l) => l.studio_id === form.studioId).map((l) => l.instructor_id));
    } else {
      ids = uniq(lessons.map((l) => l.instructor_id));
    }
    const opts = ids.sort(byInstructorRank).map((id) => ({ id, label: uname(id) }));
    return withCurrent(opts, form.instId, editCancel ? cname(editCancel) : uname(form.instId));
  }, [form.studioId, form.date, form.instId, lessons, cancellations, editId, byInstructorRank, uname, cname, editCancel]);

  const studioOptions = useMemo(() => {
    const ids = form.instId
      ? uniq(lessons.filter((l) => l.instructor_id === form.instId).map((l) => l.studio_id))
      : studios.map((s) => s.id);
    const opts = [...ids].sort(byStudioRank).map((id) => ({ id, label: studioName(id) }));
    const current = studioName(form.studioId) || (editCancel && editCancel.studio_name) || "";
    return withCurrent(opts, form.studioId, current);
  }, [form.instId, form.studioId, lessons, studios, byStudioRank, studioName, editCancel]);

  // 休校日の候補。レッスンが閉講済みなどで開催日が引けない休校は、
  // そのままだと現在値しか出せず日付を直せないので、同じ曜日の今後26週を候補にする。
  const dateCandidates = useCallback(
    (instId, studioId, currentDate) => {
      if (!(instId && studioId)) return [];
      const opts = validCancelDates(lessons, cancellations, instId, studioId, editId, today);
      if (opts.length || !currentDate) return opts;
      return fallbackDatesFor(dowOf(currentDate), cancellations, instId, studioId, editId, today);
    },
    [lessons, cancellations, editId, today]
  );

  const dateOptions = useMemo(() => {
    if (!(form.instId && form.studioId)) {
      return form.date ? [{ date: form.date, label: shortDate(form.date) }] : [];
    }
    const opts = dateCandidates(form.instId, form.studioId, form.date);
    if (form.date && !opts.some((o) => o.date === form.date)) {
      return [{ date: form.date, label: `${shortDate(form.date)}（現在）` }, ...opts];
    }
    return opts;
  }, [form.instId, form.studioId, form.date, dateCandidates]);

  const reschedOptions = useMemo(() => {
    if (!form.date) return [];
    const fifths = fifthWeekdayDates(dowOf(form.date), today, 12).filter((d) => d !== form.date);
    return [
      { value: "refund", label: "返金対応" },
      { value: "none5", label: "5週目のため振替なし" },
      ...fifths.map((d) => {
        const dt = parseYmd(d);
        return { value: d, label: `${dt.getMonth() + 1}/${dt.getDate()}（${DOW_JP[dt.getDay()]}）` };
      }),
    ];
  }, [form.date, today]);

  const targetLessons = useMemo(() => {
    if (!(form.instId && form.studioId && form.date)) return [];
    const wd = dowOf(form.date);
    return lessons
      .filter((l) => l.instructor_id === form.instId && l.studio_id === form.studioId && DOW[l.day_of_week] === wd)
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  }, [form.instId, form.studioId, form.date, lessons]);

  // その日の既存休講が覆っているクラス（"all" = 終日休講が既にある）。
  const coveredLessonIds = useMemo(() => {
    if (!(form.instId && form.studioId && form.date)) return new Set();
    return coveredOn(cancellations, form.instId, form.studioId, form.date, editId);
  }, [cancellations, form.instId, form.studioId, form.date, editId]);

  const isTaken = useCallback(
    (lessonId) => coveredLessonIds === "all" || coveredLessonIds.has(lessonId),
    [coveredLessonIds]
  );

  // まだ休講になっていない = 選択できるクラス。
  const selectableLessons = useMemo(
    () => targetLessons.filter((l) => !isTaken(l.id)),
    [targetLessons, isTaken]
  );

  // 新規登録では「選択できるクラス全部」を既定にする（= 従来どおりの終日休校）。
  // 依存に配列そのものを置くと毎レンダーで再実行されるので ID 文字列を鍵にする。
  const selectableKey = selectableLessons.map((l) => l.id).join(",");
  useEffect(() => {
    if (modalMode === "edit") return;
    const ids = selectableKey ? selectableKey.split(",").map(Number) : [];
    setForm((f) => ({ ...f, lessonIds: ids }));
  }, [selectableKey, modalMode]);

  const toggleLesson = useCallback((lessonId) => {
    setForm((f) => ({
      ...f,
      lessonIds: f.lessonIds.includes(lessonId)
        ? f.lessonIds.filter((x) => x !== lessonId)
        : [...f.lessonIds, lessonId],
    }));
  }, []);

  // ---------- モーダル：開閉 ----------
  const openAdd = useCallback(
    (prefill = {}) => {
      setModalMode("add");
      setEditId(null);
      setEditCancel(null);
      setForm({
        instId: prefill.instId || filterInstructor || null,
        studioId: prefill.studioId || filterStudio || null,
        date: prefill.date || null,
        resched: "",
        reason: "",
        lessonIds: [], // 選択可能なクラスが確定した時点で全選択に初期化される
      });
      setModalOpen(true);
    },
    [filterInstructor, filterStudio]
  );

  const openEdit = useCallback((c) => {
    setModalMode("edit");
    setEditId(c.id);
    setEditCancel(c);
    const resched = c.kind === "reschedule" ? c.rescheduled_date : c.kind === "refund" ? "refund" : c.kind === "none5" ? "none5" : "";
    setForm({
      instId: c.instructor_id,
      studioId: c.studio_id,
      date: c.cancellation_date,
      resched,
      reason: c.kind === "none5" ? "" : c.message || "",
      // 対象クラスは編集不可（変えたい場合は削除して登録し直す）。
      lessonIds: c.lesson_id ? [c.lesson_id] : [],
    });
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setModalOpen(false), []);

  // ---------- モーダル：変更ハンドラ（下流の妥当性を検証してクリア）----------
  const onInst = (v) => {
    const instId = v ? Number(v) : null;
    if (instId === form.instId) return; // 同じ値を選び直しただけなら下流を消さない
    const stOpts = instId ? uniq(lessons.filter((l) => l.instructor_id === instId).map((l) => l.studio_id)) : studios.map((s) => s.id);
    const studioId = stOpts.includes(form.studioId) ? form.studioId : null;
    const date = dateCandidates(instId, studioId, form.date).some((o) => o.date === form.date) ? form.date : null;
    setForm((f) => ({ ...f, instId, studioId, date, resched: date ? f.resched : "" }));
  };
  const onStudio = (v) => {
    const studioId = v ? Number(v) : null;
    if (studioId === form.studioId) return; // 同上
    const date = dateCandidates(form.instId, studioId, form.date).some((o) => o.date === form.date) ? form.date : null;
    setForm((f) => ({ ...f, studioId, date, resched: date ? f.resched : "" }));
  };
  const onDate = (v) => setForm((f) => ({ ...f, date: v || null, resched: "" }));
  const onResched = (v) => setForm((f) => ({ ...f, resched: v || "" }));

  const canSave =
    form.instId &&
    form.studioId &&
    form.date &&
    form.resched &&
    (modalMode === "edit" || !selectableLessons.length || form.lessonIds.length > 0);

  // ---------- 保存・削除 ----------
  const submit = async () => {
    if (!canSave) return;
    const type = form.resched === "refund" ? "refund" : form.resched === "none5" ? "none5" : "reschedule";
    const isEdit = modalMode === "edit";
    const payload = {
      studio_id: form.studioId,
      instructor_id: form.instId,
      cancellation_date: form.date,
      resched: { type, date: type === "reschedule" ? form.resched : null },
      message: form.resched === "none5" ? "" : form.reason,
    };
    // 全クラス選択ならサーバ側で lesson_id=NULL の 1 行（終日休校）に丸められる。
    // 編集では対象クラスを変更しないので送らない。
    if (!isEdit) payload.lesson_ids = form.lessonIds;
    if (isEdit) payload.cancel_id = editId;
    try {
      const res = await fetch(`${apiUrl}/api/${isEdit ? "update_cancellation" : "create_cancellation"}/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.message || "保存に失敗しました");
        return;
      }
      showToast(isEdit ? "休講を更新しました" : "休講を登録しました");
      closeModal();
      loadCancellations();
    } catch (e) {
      console.error(e);
      showToast("保存に失敗しました");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`${shortDate(c.cancellation_date)}　${studioName(c.studio_id)} / ${c.instructor_username || uname(c.instructor_id)} の休校を削除します。よろしいですか？`)) return;
    try {
      const res = await fetch(`${apiUrl}/api/delete_cancellation/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cancel_id: c.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.message || "削除に失敗しました");
        return;
      }
      showToast("削除しました");
      if (editId === c.id) closeModal();
      loadCancellations();
    } catch (e) {
      console.error(e);
      showToast("削除に失敗しました");
    }
  };

  // ---------- 月移動 ----------
  const prevMonth = () => {
    setSelDate(null);
    setMonth((m) => (m === 0 ? 11 : m - 1));
    if (month === 0) setYear((y) => y - 1);
  };
  const nextMonth = () => {
    setSelDate(null);
    setMonth((m) => (m === 11 ? 0 : m + 1));
    if (month === 11) setYear((y) => y + 1);
  };
  // 月入力（YYYY-MM）で直接切り替え
  const monthInputValue = `${year}-${pad(month + 1)}`;
  const onMonthInput = (v) => {
    if (!v) return;
    const [y, m] = v.split("-").map(Number);
    if (!y || !m) return;
    setSelDate(null);
    setYear(y);
    setMonth(m - 1);
  };
  // 今月へジャンプ
  const goThisMonth = () => {
    setSelDate(null);
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };
  const isThisMonth = year === today.getFullYear() && month === today.getMonth();

  const pillOf = (kind) =>
    kind === "reschedule" ? { cls: "c", txt: "振替" } : kind === "refund" ? { cls: "rf", txt: "返金" } : kind === "none5" ? { cls: "nn", txt: "振替なし" } : { cls: "pd", txt: "未処理" };

  const reasonHidden = form.resched === "none5";

  return (
    <div className="lesson-cancellation-page">
      {/* ヘッダー */}
      <div className="lc-head">
        <div className="lc-head-title">
          <h1>休校振替管理</h1>
        </div>
        <div className="lc-summary">
          <div className="lc-stat is-cancel">
            <span className="lc-num">{summary.cancel}</span>
            <span className="lc-lbl">今月の休校</span>
          </div>
          <div className="lc-stat is-resched">
            <span className="lc-num">{summary.resched}</span>
            <span className="lc-lbl">今月の振替</span>
          </div>
        </div>
      </div>

      {/* ツールバー */}
      <div className="lc-toolbar">
        <div className="lc-field">
          <label>スタジオ</label>
          <select
            value={filterStudio || ""}
            onChange={(e) => {
              setFilterStudio(e.target.value ? Number(e.target.value) : null);
              setSelDate(null);
            }}
          >
            <option value="">全店舗</option>
            {studioGroups.map(([pref, list]) => (
              <optgroup key={pref} label={pref}>
                {list.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="lc-field">
          <label>インストラクター</label>
          <select
            value={filterInstructor || ""}
            onChange={(e) => {
              setFilterInstructor(e.target.value ? Number(e.target.value) : null);
              setSelDate(null);
            }}
          >
            <option value="">全インストラクター</option>
            {instructors.map((i) => (
              <option key={i.id} value={i.id}>
                {i.username}
              </option>
            ))}
          </select>
        </div>
        <div className="lc-field">
          <label>表示月</label>
          <div className="lc-month-row">
            <div className="lc-month-nav">
              <button onClick={prevMonth} aria-label="前の月">
                ‹
              </button>
              <input
                type="month"
                className="lc-month-input"
                value={monthInputValue}
                onChange={(e) => onMonthInput(e.target.value)}
                aria-label="表示月"
              />
              <button onClick={nextMonth} aria-label="次の月">
                ›
              </button>
            </div>
            <button
              className="lc-btn ghost lc-today-btn"
              onClick={goThisMonth}
              disabled={isThisMonth}
            >
              今月
            </button>
          </div>
        </div>
        <div className="lc-spacer" />
        <button className="lc-btn primary" onClick={() => openAdd({})}>
          <span className="lc-plus">＋</span>休講を追加
        </button>
      </div>

      <div className="lc-grid">
        {/* カレンダー */}
        <div className="lc-card">
          <div className="lc-card-head">
            <h2>{filterStudio ? studioName(filterStudio) : "全店舗"}</h2>
            <div className="lc-legend">
              <span className="lc-tag k-resched">振替</span>
              <span className="lc-tag k-refund">返金</span>
              <span className="lc-tag k-dst">振替先</span>
              <span className="lc-tag k-none5">5週目</span>
            </div>
          </div>
          <div className="lc-cal-scroll">
            <div className="lc-cal">
              <div className="lc-cal-dow">
                {DOW_JP.map((d, i) => (
                  <div key={d} className={i === 0 ? "sun" : i === 6 ? "sat" : ""}>
                    {d}
                  </div>
                ))}
              </div>
              <div className="lc-cal-body">
                {calendarCells.map((cell) => {
                  if (cell.blank) return <div key={cell.key} className="lc-cell blank" />;
                  const { ds, d, dow, dayCancels, dayResched } = cell;
                  const dayItems = [
                    ...dayCancels.map((c) => ({ t: "c", c })),
                    ...dayResched.map((c) => ({ t: "r", c })),
                  ];
                  const isToday = ds === todayStr;
                  const isSel = ds === selDate;
                  const hasLesson = filterStudio && studioLessonsOn(filterStudio, ds).length > 0;
                  const classes = ["lc-cell"];
                  if (isToday) classes.push("today");
                  if (isSel) classes.push("sel");
                  if (filterStudio ? hasLesson : true) classes.push("addable");

                  const onCellClick = () => {
                    if (filterStudio) {
                      if (hasLesson) openAdd({ studioId: filterStudio, date: ds });
                    } else {
                      setSelDate(ds);
                      setFocusedCancelId(null);
                    }
                  };

                  return (
                    <div key={cell.key} className={classes.join(" ")} onClick={onCellClick}>
                      <div className="lc-daynum" style={dow === 0 ? { color: "#d98b86" } : dow === 6 ? { color: "#86a9d9" } : undefined}>
                        {d}
                        {isToday && <span className="lc-todaytag">今日</span>}
                      </div>

                      {dayItems.map((it, idx) => (
                        <div
                          key={`${it.t}${it.c.id}${idx}`}
                          className={`lc-chip ${chipVariant(it)}`}
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            focusCancellation(it.c, ds);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              focusCancellation(it.c, ds);
                            }
                          }}
                        >
                          <span className="lc-mk">{it.t === "c" ? "×" : "○"}</span>
                          <span className="lc-who" title={`${filterStudio ? cname(it.c) : studioName(it.c.studio_id)}${it.c.lesson_name ? ` / ${it.c.lesson_name}` : ""}`}>
                            {filterStudio ? cname(it.c) : studioName(it.c.studio_id)}
                            {it.c.lesson_name ? ` / ${it.c.lesson_name}` : ""}
                          </span>
                        </div>
                      ))}
                      {filterStudio && hasLesson && <span className="lc-add-hint">＋</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* 一覧 */}
        <div className="lc-card">
          <div className="lc-card-head">
            <h2>{selDate ? `${shortDate(selDate)} の一覧` : "今月の一覧"}</h2>
          </div>
          <div className="lc-list">
            {listRows.length === 0 ? (
              <div className="lc-list-empty">該当する休校振替はありません</div>
            ) : (
              listRows.map((c) => {
                const pill = pillOf(c.kind);
                return (
                  <div
                    key={c.id}
                    className={`lc-row${c.id === focusedCancelId ? " focused" : ""}`}
                    onClick={() => {
                      setSelDate(c.cancellation_date);
                      setFocusedCancelId(null);
                    }}
                  >
                    <div className={`lc-row-pill ${pill.cls}`}>{pill.txt}</div>
                    <div className="lc-row-main">
                      <div className="lc-row-dates">
                        {shortDate(c.cancellation_date)}
                        {c.kind === "reschedule" ? (
                          <>
                            <span className="arrow">→</span>
                            {shortDate(c.rescheduled_date)}
                          </>
                        ) : c.kind === "refund" ? (
                          <>
                            <span className="arrow">→</span>
                            <span className="refund">返金対応</span>
                          </>
                        ) : c.kind === "none5" ? (
                          <>
                            <span className="arrow">→</span>
                            <span className="nn">5週目のため振替なし</span>
                          </>
                        ) : (
                          <>
                            <span className="arrow">→</span>
                            <span className="nn">未処理</span>
                          </>
                        )}
                      </div>
                      <div className="lc-row-meta">
                        {!filterStudio && <span className="studio">{studioName(c.studio_id)}</span>}
                        <span className="user">{cname(c)}</span>
                        {/* lesson_id が入っている行はそのクラスだけの休講 */}
                        <span className="lesson">{c.lesson_name || "全クラス"}</span>
                      </div>
                      {c.message && c.kind !== "none5" && <div className="lc-row-msg">{c.message}</div>}
                    </div>
                    <div className="lc-row-actions">
                      <button
                        className="lc-icon-btn"
                        title="編集"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(c);
                        }}
                      >
                        ✎
                      </button>
                      <button
                        className="lc-icon-btn del"
                        title="削除"
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(c);
                        }}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* モーダル */}
      {modalOpen && (
        <div className="lc-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="lc-modal" role="dialog" aria-modal="true">
            <div className="lc-modal-head">
              <h3>{modalMode === "edit" ? "休校を編集" : "休講を追加"}</h3>
              <button className="lc-x" onClick={closeModal} aria-label="閉じる">
                ×
              </button>
            </div>
            <div className="lc-modal-body">
              <div className="lc-steps">
                <b>インストラクター</b>
                <span className="sep">›</span>
                <b>スタジオ</b>
                <span className="sep">›</span>
                <b>休校日</b>
                <span className="sep">›</span>
                <b>振替</b>
              </div>

              <div className="lc-m-field">
                <label>
                  インストラクター <span className="req">必須</span>
                </label>
                <select value={form.instId || ""} onChange={(e) => onInst(e.target.value)}>
                  <option value="">— 選択 —</option>
                  {instOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="lc-m-field">
                <label>
                  スタジオ <span className="req">必須</span>
                </label>
                <select value={form.studioId || ""} onChange={(e) => onStudio(e.target.value)}>
                  <option value="">— 選択 —</option>
                  {studioOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="lc-m-field">
                <label>
                  休校日 <span className="req">必須</span>
                </label>
                <select value={form.date || ""} onChange={(e) => onDate(e.target.value)} disabled={!(form.instId && form.studioId) && dateOptions.length === 0}>
                  <option value="">{!(form.instId && form.studioId) ? "— インストラクター・スタジオを選択 —" : "— 選択 —"}</option>
                  {dateOptions.map((o) => (
                    <option key={o.date} value={o.date}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {modalMode === "edit" ? (
                  <div className="lc-hint">
                    対象クラス：{editCancel && editCancel.lesson_id ? editCancel.lesson_name || `#${editCancel.lesson_id}` : "全クラス"}
                    （変更するには一度削除して登録し直してください）
                  </div>
                ) : targetLessons.length > 0 ? (
                  <div className="lc-lessonpick">
                    <div className="lc-lessonpick-head">休校にするクラス</div>
                    {targetLessons.map((l) => {
                      const taken = isTaken(l.id);
                      return (
                        <label key={l.id} className={taken ? "taken" : ""}>
                          <input
                            type="checkbox"
                            disabled={taken}
                            checked={!taken && form.lessonIds.includes(l.id)}
                            onChange={() => toggleLesson(l.id)}
                          />
                          <span className="nm">
                            {l.start_time} {l.name}
                          </span>
                          {taken && <em>登録済み</em>}
                        </label>
                      );
                    })}
                    <div className="lc-hint">
                      一部のクラスだけ選ぶと、選んだクラスのみ休講になります。残りは別の振替日で改めて登録してください。
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lc-m-field">
                <label>
                  振替日 <span className="req">必須</span>
                </label>
                <select value={form.resched} onChange={(e) => onResched(e.target.value)} disabled={!form.date}>
                  <option value="">{form.date ? "— 選択 —" : "— 休校日を選択 —"}</option>
                  {reschedOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              {!reasonHidden && (
                <div className="lc-m-field">
                  <label>
                    休校理由 <span className="opt">（任意）</span>
                  </label>
                  <textarea
                    value={form.reason}
                    onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                    placeholder="例）インストラクター都合 / 会場都合 など"
                  />
                </div>
              )}
            </div>
            <div className="lc-modal-foot">
              {modalMode === "edit" ? (
                <button className="lc-btn danger" onClick={() => editCancel && remove(editCancel)}>
                  削除
                </button>
              ) : (
                <span />
              )}
              <div className="right">
                <button className="lc-btn ghost" onClick={closeModal}>
                  キャンセル
                </button>
                <button className="lc-btn primary" onClick={submit} disabled={!canSave}>
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="lc-toast show">{toast}</div>}
    </div>
  );
}

export default LessonCancellationPage;
